/**
 * Product and User types for shell app
 *
 * These are defined locally to ensure consistency with the backend API contract
 * and to avoid issues with type-only exports from @mysaiva/core.
 *
 * Note: These definitions match the backend API (types/api.ts in core)
 * but are maintained separately to avoid TypeScript module resolution issues.
 */

/**
 * Available SAIVA products
 * These values must match the backend API enum
 */
export enum SaivaProduct {
  Upt = 'upt',
  WoundCare = 'wound_care',
  Therapy = 'therapy',
  Compass = 'compass'
}

/**
 * User data from /users/me endpoint
 * This interface matches the backend UserMeRead schema
 */
export interface UserMeRead {
  id: number
  email?: string | null
  username?: string | null
  password_expires_at?: string | null
  internal_id: string
  name: string
  is_superuser: boolean
  product_list: SaivaProduct[]
}

// Use the same localStorage key as the core library to maintain consistency
// with ProductSwitcher and other components
const LAST_SELECTED_PRODUCT_KEY = 'mysaiva-last-app'

// Product to URL mapping
// Note: These paths must match the routes defined in App.tsx and the paths
// used by the micro-frontend applications
const PRODUCT_URLS: Record<SaivaProduct, string> = {
  [SaivaProduct.Upt]: '/upt',
  [SaivaProduct.WoundCare]: '/wounds', // Note: enum is 'wound_care' but path is '/wounds'
  [SaivaProduct.Therapy]: '/therapy',
  [SaivaProduct.Compass]: '/compass'
}

/**
 * Get the last selected product from localStorage
 */
export function getLastSelectedProduct(): SaivaProduct | null {
  try {
    const stored = localStorage.getItem(LAST_SELECTED_PRODUCT_KEY)
    return stored as SaivaProduct | null
  } catch (error) {
    console.error('Error reading last selected product from localStorage:', error)
    return null
  }
}

/**
 * Save the selected product to localStorage
 */
export function saveLastSelectedProduct(product: SaivaProduct): void {
  try {
    localStorage.setItem(LAST_SELECTED_PRODUCT_KEY, product)
  } catch (error) {
    console.error('Error saving last selected product to localStorage:', error)
  }
}

/**
 * Get available products from user data
 * Products are determined by the product_list in user data
 */
export function getAvailableProducts(user: UserMeRead): SaivaProduct[] {
  // Use product_list from user data
  return user.product_list || []
}

/**
 * Determine which product to redirect to based on:
 * 1. Last selected product (if still available)
 * 2. First available product
 */
export function determineRedirectProduct(user: UserMeRead): SaivaProduct | null {
  const availableProducts = getAvailableProducts(user)

  if (availableProducts.length === 0) {
    return null
  }

  // Check if last selected product is still available
  const lastSelected = getLastSelectedProduct()
  if (lastSelected && availableProducts.includes(lastSelected)) {
    return lastSelected
  }

  // Default to first available product
  return availableProducts[0]
}

/**
 * Get the redirect URL for a product
 */
export function getProductUrl(product: SaivaProduct): string {
  return PRODUCT_URLS[product] || '/'
}

/**
 * Redirect to the appropriate product based on user data
 */
export function redirectToProduct(user: UserMeRead): void {
  const product = determineRedirectProduct(user)

  if (!product) {
    console.error('No available products for user')
    return
  }

  const url = getProductUrl(product)
  saveLastSelectedProduct(product)

  // Perform the redirect
  window.location.href = url
}
